import { useMemo } from 'react';

import { useTimeToExpire, useUserDetails } from '@/features/_shared/user';
import { formatDaysLeft } from '@/shared/lib/format/formatString';
import { useAppSelector } from '@/store/hooks';

const ExpiryCountdown = () => {
  const { userAccess, subscription } = useAppSelector(state => state.tripToken);

  const { isTrial, isUserExpired } = useUserDetails();
  const timeToExpire = useTimeToExpire();

  const daysLeft = useMemo<number | null>(() => {
    if (!timeToExpire) return null;
    if (isUserExpired) return 0;

    if (
      timeToExpire.days > 0 &&
      timeToExpire.hours < 0 &&
      timeToExpire.minutes < 0 &&
      timeToExpire.seconds < 60
    ) {
      return timeToExpire.days;
    }

    return timeToExpire.days + 1; // Adding 1 to include hours, minutes left in the day
  }, [timeToExpire, isUserExpired]);

  if (daysLeft === null) return null;
  if (!isTrial && daysLeft > 14) return null;

  const progressPercentage = Math.round((Math.min(daysLeft, 14) / 14) * 100);
  const plan = subscription?.plan ?? userAccess;

  return (
    <section className='mb-6 flex h-[67px] w-full flex-col justify-center gap-1 self-center bg-gx-response px-4 py-2'>
      <div className='flex items-center justify-between text-left font-[Cairo] text-sm text-white'>
        <span>
          {plan} {isTrial ? 'trial' : 'subscription'}
        </span>
        <span className='text-right'>{formatDaysLeft(daysLeft)} left</span>
      </div>
      <div
        aria-valuemax={100}
        aria-valuemin={0}
        aria-valuenow={progressPercentage}
        className='mt-[3px] h-1 overflow-hidden bg-white/30'
        role='progressbar'
      >
        <div
          className='h-full bg-gx-highlight'
          style={{ width: `${progressPercentage}%` }}
        />
      </div>
    </section>
  );
};

export default ExpiryCountdown;
